// @ts-nocheck
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { getLatestMetricsByPlayerUuid, type MetricsData } from '@/helpers/api/metrics';
import useApi from '@/helpers/api/api';

export interface UsePlayerMetricsOptions {
  /** Reactive or static device UUID (dashed or 32-hex). */
  deviceUuid: () => string | null | undefined;
  /** Poll interval in ms. Default 10_000. */
  pollMs?: number;
  /** Window used to derive `isTimedOut` and the Status tile text. Defaults to `pollMs`. */
  timeoutMs?: number;
}

/**
 * Shared metrics polling + status derivation for the Player Editor.
 *
 * Owns the metrics fetch + thumbnail enrichment so multiple consumers
 * (Device Metrics card and the page-level Status stat tile) can read
 * the same reactive bundle without each running its own poller.
 */
export default function usePlayerMetrics(opts: UsePlayerMetricsOptions) {
  const pollMs = opts.pollMs ?? 10_000;
  const timeoutMs = opts.timeoutMs ?? pollMs;

  const metrics = ref<MetricsData | null>(null);
  const lastUpdated = ref<Date | null>(null);
  const isLoading = ref(false);
  const error = ref('');
  const nowPlayingThumbMap = ref<Record<string, string>>({});
  const now = ref<number>(Date.now());

  let pollTimer: number | null = null;
  let tickTimer: number | null = null;

  function addDashesToUuid(uuid?: string | null): string | null {
    if (!uuid) return null;
    const value = uuid.replace(/-/g, '');
    if (!/^[0-9a-fA-F]{32}$/.test(value)) return null;
    return `${value.substring(0, 8)}-${value.substring(8, 12)}-${value.substring(12, 16)}-${value.substring(16, 20)}-${value.substring(20)}`.toLowerCase();
  }

  async function fetchLatest() {
    const playerUuid = addDashesToUuid(opts.deviceUuid());
    if (!playerUuid) {
      error.value = 'Device UUID not available';
      return;
    }
    isLoading.value = true;
    error.value = '';
    try {
      const data = await getLatestMetricsByPlayerUuid(playerUuid, 1);
      metrics.value = data && data.length > 0 ? data[0] : null;
      lastUpdated.value = metrics.value ? new Date(metrics.value.created_at) : null;
      const currentlyPlaying = metrics.value?.currently_playing || [];
      if (Array.isArray(currentlyPlaying) && currentlyPlaying.length > 0) {
        const items = currentlyPlaying.map((item) => {
          if (typeof item === 'string') {
            return { name: item, url: '' };
          }
          return { name: item?.contentTitle || item?.title || '', url: item?.contentUrl || '' };
        }).filter((i) => i.name);
        loadThumbnailsForNowPlaying(items);
      } else {
        nowPlayingThumbMap.value = {};
      }
    } catch (e: any) {
      error.value = e?.message || 'Failed to load metrics';
    } finally {
      isLoading.value = false;
    }
  }

  function startPolling() {
    stopPolling();
    pollTimer = window.setInterval(fetchLatest, pollMs);
    // Tick ~ every second so `isTimedOut` re-evaluates even when no new metrics arrive.
    tickTimer = window.setInterval(() => { now.value = Date.now(); }, 1000);
  }

  function stopPolling() {
    if (pollTimer) { window.clearInterval(pollTimer); pollTimer = null; }
    if (tickTimer) { window.clearInterval(tickTimer); tickTimer = null; }
  }

  onMounted(() => {
    fetchLatest();
    startPolling();
  });

  onUnmounted(() => {
    stopPolling();
  });

  watch(() => opts.deviceUuid(), () => {
    fetchLatest();
  });

  // -------- Currently-playing helpers (lifted from the original component) --------

  const nowPlayingList = computed<string[]>(() => {
    const list = metrics.value?.currently_playing || [];
    if (!Array.isArray(list)) return [];
    return list
      .map((item) => (typeof item === 'string' ? item : item.contentTitle || item.title || ''))
      .filter(Boolean);
  });

  const nowPlayingItems = computed<{ name: string; url: string }[]>(() => {
    const list = metrics.value?.currently_playing || [];
    if (!Array.isArray(list)) return [];
    return list
      .map((item) => {
        if (typeof item === 'string') return { name: item, url: '' };
        return { name: item?.contentTitle || item?.title || '', url: item?.contentUrl || '' };
      })
      .filter((i) => i.name);
  });

  function getBaseName(input: string): string {
    if (!input) return '';
    const parts = input.split(/[\\/]/g);
    return parts[parts.length - 1];
  }
  function stripExtension(input: string): string {
    if (!input) return '';
    const idx = input.lastIndexOf('.');
    return idx > 0 ? input.substring(0, idx) : input;
  }
  function normalizeForCompare(input: string): string {
    return (input || '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[_-]+/g, '')
      .trim();
  }

  function extractUuidFromUrl(url: string): string | null {
    if (!url) return null;
    try {
      const withoutQuery = url.split('?')[0] || url;
      const parts = withoutQuery.split('/');
      const last = parts[parts.length - 1] || '';
      const base = stripExtension(last);
      const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
      return guidRegex.test(base) ? base : null;
    } catch (_) {
      return null;
    }
  }

  async function searchAssetsByName(term: string): Promise<any[]> {
    try {
      const { post } = useApi();
      const listRequestOptions = {
        limit: 10,
        offset: 0,
        query: { fieldNames: ['Name'], term },
        filters: [],
        sorts: [{ fieldName: 'DateLastModified', isDescending: true }],
      };
      const { responseBody } = await post('assetsearch', listRequestOptions, { isLogoutOnInvalidAuth: true });
      return responseBody?.results || [];
    } catch (e) {
      console.error('[usePlayerMetrics] searchAssetsByName failed for term:', term, e);
      return [];
    }
  }

  async function searchAssetsByPublicName(publicName: string): Promise<any[]> {
    try {
      const { post } = useApi();
      const listRequestOptions = {
        limit: 10,
        offset: 0,
        query: { fieldNames: ['PublicName'], term: publicName },
        filters: [],
        sorts: [{ fieldName: 'DateLastModified', isDescending: true }],
      };
      const { responseBody } = await post('assetsearch', listRequestOptions, { isLogoutOnInvalidAuth: true });
      return responseBody?.results || [];
    } catch (e) {
      console.error('[usePlayerMetrics] searchAssetsByPublicName failed for publicName:', publicName, e);
      return [];
    }
  }

  async function getAssetLinksPreviewUrl(assetId: number | string): Promise<string | null> {
    try {
      const { post } = useApi();
      const path = `api/assets/${assetId}/AssetLinks`;
      let result = await post(path, {}, { isLogoutOnInvalidAuth: true });
      let preview = result?.responseBody?.previewUrl || result?.responseBody?.previewUri || null;
      if (preview) return preview;
      result = await post(path, {}, { isLogoutOnInvalidAuth: true });
      preview = result?.responseBody?.previewUrl || result?.responseBody?.previewUri || null;
      if (!preview) {
        console.warn('[usePlayerMetrics] AssetLinks returned no previewUrl for assetId:', assetId, result?.responseBody);
      }
      return preview;
    } catch (e) {
      console.error('[usePlayerMetrics] AssetLinks request failed', assetId, e);
      return null;
    }
  }

  async function loadThumbnailsForNowPlaying(items: { name: string; url: string }[]) {
    try {
      if (!items || items.length === 0) return;
      const uniqueItems = [] as { name: string; url: string }[];
      const seen = new Set<string>();
      for (const it of items) {
        if (it?.name && !seen.has(it.name)) { uniqueItems.push(it); seen.add(it.name); }
      }

      const fetchCandidatesByName = async (term: string) => {
        try { return (await searchAssetsByName(term)) || []; }
        catch (_) { return [] as any[]; }
      };

      const thumbMap: Record<string, string> = {};

      for (const { name: originalName, url } of uniqueItems) {
        const base = getBaseName(originalName);
        const nameNoExt = stripExtension(base);
        const normOriginal = normalizeForCompare(originalName);
        const normBase = normalizeForCompare(base);
        const normNoExt = normalizeForCompare(nameNoExt);

        let candidates: any[] = [];
        const uuid = extractUuidFromUrl(url);
        if (uuid) candidates = await searchAssetsByPublicName(uuid);
        if (!candidates.length) candidates = await fetchCandidatesByName(base);
        if (!candidates.length && nameNoExt) candidates = await fetchCandidatesByName(nameNoExt);
        if (!candidates.length) candidates = await fetchCandidatesByName(originalName);

        let picked: any = null;
        let bestScore = -1;
        for (const item of candidates) {
          const itemName: string = item?.name || item?.Name || '';
          const normItem = normalizeForCompare(itemName);
          let score = 0;
          if (normItem === normOriginal) score = 100;
          else if (normItem === normBase) score = 95;
          else if (normItem === normNoExt) score = 90;
          else if (normItem.includes(normNoExt) && normNoExt.length > 0) score = 70;
          else if (normItem.includes(normBase) && normBase.length > 0) score = 60;
          if (score > bestScore) { bestScore = score; picked = item; }
        }

        let previewUri = picked?.previewUri
          || picked?.versions?.[0]?.resource?.publicUrl
          || picked?.Versions?.[0]?.PublicUrl
          || null;

        if (!previewUri) {
          const tryIds: Array<number | string> = [];
          const pickedId = picked?.id ?? picked?.Id ?? picked?.assetId ?? picked?.AssetId;
          if (pickedId != null) tryIds.push(pickedId);
          for (const c of candidates) {
            const cid = c?.id ?? c?.Id ?? c?.assetId ?? c?.AssetId;
            if (cid != null && !tryIds.includes(cid)) tryIds.push(cid);
          }
          for (const aid of tryIds) {
            const link = await getAssetLinksPreviewUrl(aid);
            if (link) { previewUri = link; break; }
          }
        }

        if (previewUri) thumbMap[originalName] = previewUri;
        else console.warn('[usePlayerMetrics] No thumbnail found for', originalName);
      }
      nowPlayingThumbMap.value = thumbMap;
    } catch (_) {
      void 0;
    }
  }

  watch(nowPlayingItems, (items) => {
    loadThumbnailsForNowPlaying(items);
  });

  // -------- Status derivation --------

  /** True when no fresh metric row has arrived within `timeoutMs`. */
  const isTimedOut = computed(() => {
    if (!lastUpdated.value) return true;
    return (now.value - lastUpdated.value.getTime()) > timeoutMs;
  });

  const statusText = computed(() => (isTimedOut.value ? 'Timed Out' : 'Online'));
  const statusDetail = computed(() => (isTimedOut.value ? `${timeoutMs}ms exceeded` : ''));

  return {
    metrics,
    lastUpdated,
    isLoading,
    error,
    pollMs,
    timeoutMs,
    isTimedOut,
    statusText,
    statusDetail,
    nowPlayingList,
    nowPlayingItems,
    nowPlayingThumbMap,
    refresh: fetchLatest,
  };
}
